import React, { useMemo, useState } from "react";
import { Checkbox, Upload } from "antd";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import CustomModal from "../../../Core/CommonV2/CustomModal";
import CustomSearchBox from "../../../Core/CommonV2/CustomSearchBox";
import CustomButton from "../../../Core/CommonV2/CustomButton";
import CustomInputBox from "../../../Core/CommonV2/CustomInputBox";
import { CrossIconSecondary } from "../../../Core/svgV2/CrossIcon";
import useSearch from "../../../Core/hooks/useSearch";
import uniqid from "uniqid";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import { updateConversations } from "../../../Core/redux/slices/conversations.slice";
import { getThreads } from "../../../Core/redux/api/conversationAPI";
import ImageCropModal from "../../../Core/CommonV3/ImageCropModal";
import { createGroup, getThreadsMembers } from "../../../Core/redux/api/v3/conversationAPI";
import { updateDashboard } from "../../../Core/redux/slices/dashboard.slice";

const GroupCheckboxStyles = () => (
  <style>{`
    .wa-group-checkbox .ant-checkbox-inner {
      width: 18px !important;
      height: 18px !important;
      border-radius: 2px !important;
      border: 2px solid #667085 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background-color: transparent !important;
    }
    .wa-group-checkbox .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #165ABF !important;
      border-color: #165ABF !important;
    }
    .wa-group-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
      content: '' !important;
      position: static !important;
      border: none !important;
      transform: none !important;
      inset: auto !important;
      margin: 0 !important;
      width: 9px !important;
      height: 7px !important;
      background-image: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='9'%20height='7'%20viewBox='0%200%2012%209.4'%3E%3Cpath%20d='M1%204.9L4.3%208.2L11%201'%20stroke='white'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%20fill='none'/%3E%3C/svg%3E") !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 9px 7px !important;
    }
    .wa-group-checkbox:hover .ant-checkbox-inner {
      border-color: #165ABF !important;
    }
    .wa-group-checkbox .ant-checkbox-input:focus-visible + .ant-checkbox-inner {
      border-color: #165ABF !important;
    }
  `}</style>
);

const GroupScrollbarStyles = () => (
  <style>{`
    .wa-group-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .wa-group-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .wa-group-scroll::-webkit-scrollbar-thumb {
      background-color: #D0D5DD;
      border-radius: 4px;
    }
    .wa-group-scroll::-webkit-scrollbar-thumb:hover {
      background-color: #98A2B3;
    }
    .wa-group-scroll {
      scrollbar-width: thin;
      scrollbar-color: #D0D5DD transparent;
    }
  `}</style>
);

const GroupNameInputStyles = () => (
  <style>{`
    .wa-group-name-input.border-primary-500 {
      border-color: #7DC7FF !important;
    }
    .wa-group-name-input.shadow-input-focus {
      box-shadow: 0 0 0 2px #D1E9FF !important;
    }
    .wa-group-name-input.border-grey-200:hover {
      border-color: #7DC7FF !important;
    }
  `}</style>
);

// Same highlight tokens as GroupNameInputStyles (#7DC7FF border / #D1E9FF
// focus ring), applied to the search box's outer wrapper. CustomSearchBox
// puts its border classes directly on that wrapper div rather than exposing
// a wrapperClassName like CustomInputBox does, so focus state is tracked
// here and toggled into the className we pass down instead.
const GroupSearchInputStyles = () => (
  <style>{`
    .wa-group-search-input:hover {
      border-color: #7DC7FF !important;
    }
    .wa-group-search-input.wa-group-search-focused {
      border-color: #7DC7FF !important;
      box-shadow: 0 0 0 2px #D1E9FF !important;
    }
  `}</style>
);

// ── List item — Figma "List item" component, "Text Headline" child:
// Outfit, 600 SemiBold, 16px, line-height 24px, letter-spacing 0%, #292927.
const ContactRow = ({ contact, checked, onCheck }) => (
  <div
    className="flex items-center justify-between h-[56px] min-h-[56px] rounded-[8px] cursor-pointer transition-colors -mx-2 px-2 hover:bg-[#EEF3FB]"
    onClick={() => onCheck(contact.userId)}
  >
    <div className="flex items-center gap-x-3 min-w-0">
      <CustomAvatar
        title={`${contact.firstname} ${contact.lastname}`}
        src={contact.profilePicture}
        size={40}
        whiteText
        fontSize="16px"
        color={contact.profilePictureColorCode}
      />
      <span className="font-['Outfit'] font-semibold text-[16px] leading-[24px] text-[#292927] truncate">
        {contact.firstname} {contact.lastname}
      </span>
    </div>
    <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
      <Checkbox
        className="wa-group-checkbox"
        checked={checked}
        onChange={() => onCheck(contact.userId)}
      />
    </div>
  </div>
);

const CreateGroupModal = ({ onClose: onCancel }) => {
  const dispatch = useAppDispatch();
  const { newGroupModal, editThread, directMessageUsers, threads } =
    useAppSelector((state) => state.conversations);
  const { user } = useAppSelector((state) => state.userDetails);
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  const { allowedUsersForGuest } = useAppSelector((state) => state.settings);

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [groupName, setGroupName] = useState("");
  const [image, setImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const dmContactsFromThreads = useMemo(() => {
    return (threads || [])
      .filter((t: any) => t.isGroup === false)
      .map((t: any) => ({
        userId: t.userId ?? t.userid,
        firstname: t.firstname,
        lastname: t.lastname,
        profilePicture: t.profilePicture,
        profilePictureColorCode: t.profilePictureColorCode,
      }))
      .filter((c: any) => c.userId != null && +c.userId !== +user.id);
  }, [threads, user.id]);

  const contactPool = useMemo(() => {
    const byId = new Map<string, any>();
    [...dmContactsFromThreads, ...(directMessageUsers || [])].forEach(
      (c: any) => {
        if (c?.userId != null) {
          byId.set(String(c.userId), { ...byId.get(String(c.userId)), ...c });
        }
      }
    );
    return Array.from(byId.values());
  }, [dmContactsFromThreads, directMessageUsers]);

  let contacts = contactPool;
  if (+user.roleId === 4) {
    contacts = contacts.filter((c: any) =>
      allowedUsersForGuest?.includes(c.userId)
    );
  }

  const { search, setSearch, filteredData: rawFilteredContacts } = useSearch(
    contacts,
    ["firstname", "lastname"]
  );

  const filteredContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rawFilteredContacts;

    const rank = (c: any) => {
      const first = (c.firstname || "").toLowerCase();
      const last = (c.lastname || "").toLowerCase();
      if (first.startsWith(query)) return 0;
      if (last.startsWith(query)) return 1;
      return 2;
    };

    return [...(rawFilteredContacts || [])].sort((a: any, b: any) => {
      const rankDiff = rank(a) - rank(b);
      if (rankDiff !== 0) return rankDiff;
      return (a.firstname || "").localeCompare(b.firstname || "");
    });
  }, [rawFilteredContacts, search]);

  const selectedContacts = contacts.filter((c: any) =>
    selectedIds.includes(c.userId)
  );

  const isSearching = search.trim().length > 0;

  // Section groups, in DISPLAY order. While browsing (no search), sections
  // are alphabetical (A, B, C...) as before. While searching, sections
  // follow the relevance ranking from filteredContacts instead of the
  // alphabet — a new section starts only when the letter actually changes
  // as we walk the ranked list, rather than sections being sorted A→Z.
  // That's what lets "J" (Jett Kor) appear above "A" (Anje Okr) for a
  // query like "je", while each contact still keeps its letter header.
  const letterSections: { letter: string; items: any[] }[] = useMemo(() => {
    const list = filteredContacts || [];

    if (!isSearching) {
      const groups: Record<string, any[]> = {};
      list.forEach((c: any) => {
        const letter = (c.firstname?.[0] || "#").toUpperCase();
        groups[letter] = groups[letter] || [];
        groups[letter].push(c);
      });
      return Object.keys(groups)
        .sort()
        .map((letter) => ({ letter, items: groups[letter] }));
    }

    const sections: { letter: string; items: any[] }[] = [];
    list.forEach((c: any) => {
      const letter = (c.firstname?.[0] || "#").toUpperCase();
      const last = sections[sections.length - 1];
      if (last && last.letter === letter) {
        last.items.push(c);
      } else {
        sections.push({ letter, items: [c] });
      }
    });
    return sections;
  }, [filteredContacts, isSearching]);

  const toggleContact = (id: string | number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const reset = () => {
    setStep(1);
    setSearch("");
    setSelectedIds([]);
    setGroupName("");
    setImage("");
    setFileList([]);
    setSearchFocused(false);
  };

  const onClose = () => {
    reset();
    onCancel();
  };

  const uploadImage = (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;
    const data = new FormData();
    data.append(
      "file",
      file,
      uniqid(`${file.name.split(".")[0]}-`, `.${file.name.split(".").at(-1)}`)
    );
    const config = {
      headers: { "content-type": "multipart/form-data" },
      onUploadProgress: (event: any) =>
        onProgress({ percent: (event.loaded / event.total) * 100 }),
    };
    axios.post(
      process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej", data, config)
      .then((res) => onSuccess(res.data))
      .catch((err) => onError(err));
  };

  const handleImageChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
    const done = newFileList.filter((f: any) => f.status === "done");
    const last = done[done.length - 1];
    const url = last?.response?.[0]?.fileUrl;
    if (url) setImage(url);
  };

  const handleCreateGroup = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName || creating) return;

    setCreating(true);

    const participants = selectedIds.map((id) => +id);
    participants.push(+user.id);

    const result = await createGroup({
      groupName: trimmedName,
      userId: +user.id,
      participants: JSON.stringify(participants),
      tenantId: tenantDetails.tenantId,
      profilePicture: image || "",
      senderName: `${user.firstName} ${user.lastName}`.trim(),
    });

    setCreating(false);

    if (result) {
      dispatch(
        getThreads({ userId: user.id, tenantId: tenantDetails.tenantId })
      );

      if (result?.thread) {
        const newThreadId = result.thread.threadId || result.thread.id;

        dispatch(
          updateConversations({
            key: "currentThread",
            value: {
              ...result.thread,
              threadId: newThreadId,
              type: "group",
              groupProfilePicture:
                result.thread.groupProfilePicture ||
                result.thread.profilePicture ||
                result.thread.profile_picture ||
                image ||
                "",
            },
          })
        );
        if (newThreadId) {
          dispatch(getThreadsMembers({ threadId: newThreadId }));
        }
      }

      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "New Group",
              subtitle: "",
              description: `Created a new group ${trimmedName}.`,
            },
          },
        })
      );

      onClose();
    }
  };

  const GroupUploadStyles = () => (
    <style>{`
    .wa-group-upload.ant-upload-wrapper,
    .wa-group-upload .ant-upload-select,
    .wa-group-upload .ant-upload-select .ant-upload {
      display: flex !important;
      justify-content: center !important;
      width: 100% !important;
    }
  `}</style>
  );

  const GroupModalFadeStyles = () => (
    <style>{`
    @keyframes waModalFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes waModalFadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }

    .wa-create-group-modal .ant-zoom-enter,
    .wa-create-group-modal .ant-zoom-appear {
      opacity: 0 !important;
      transform: none !important;
    }
    .wa-create-group-modal .ant-zoom-enter.ant-zoom-enter-active,
    .wa-create-group-modal .ant-zoom-appear.ant-zoom-appear-active {
      animation-name: waModalFadeIn !important;
      animation-duration: 0.2s !important;
      animation-timing-function: ease-out !important;
      transform: none !important;
    }
    .wa-create-group-modal .ant-zoom-leave.ant-zoom-leave-active {
      animation-name: waModalFadeOut !important;
      animation-duration: 0.15s !important;
      animation-timing-function: ease-in !important;
      transform: none !important;
    }
  `}</style>
  );

  return (
    <>
      <GroupCheckboxStyles />
      <GroupScrollbarStyles />
      <GroupModalFadeStyles />
      <GroupNameInputStyles />
      <GroupSearchInputStyles />
      <GroupUploadStyles />
      <CustomModal
        visible={!!newGroupModal && !editThread}
        width="376px"
        height="565px"
        maskClosable={false}
        onCancel={onClose}
        wrapClassName="wa-create-group-modal"
        body={
          <div
            className="p-4 flex flex-col gap-y-4 rounded-[12px]"
            style={{
              backgroundColor: "#F9FBFE",
              height: "565px",
              boxShadow:
                "0px 12px 16px -4px rgba(16,24,40,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)",
            }}
          >
            <div>
              <h3 className="out-500-14 text-black !mb-0 !leading-4">Create a group</h3>
              <p className="font-['Outfit'] font-light text-[14px] leading-[20px] tracking-[0%] text-[#667085]">
                Make groups for your team to communicate better.
              </p>
            </div>

            {step === 1 ? (
              <>
                <div className="pb-4 -mx-4 px-4 border-b-[0.33px] border-grey/30">
                  <CustomSearchBox
                    search={search}
                    setSearch={setSearch}
                    placeholder="Search"
                    className={`!bg-[#F0F5FA] wa-group-search-input ${searchFocused ? "wa-group-search-focused !border-[#7DC7FF]" : "!border-[#DDE5ED]"
                      }`}
                    style={{ height: "35px" }}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                  />
                </div>

                {selectedContacts.length > 0 && (
                  <div className="wa-group-scroll flex flex-wrap gap-2 max-h-[88px] overflow-y-auto">
                    {selectedContacts.map((c: any) => (
                      <div
                        key={c.userId}
                        className="flex items-center gap-x-2 h-[32px] p-1 rounded-[4px]"
                        style={{ backgroundColor: "#165ABF" }}
                      >
                        <CustomAvatar
                          title={`${c.firstname} ${c.lastname}`}
                          src={c.profilePicture}
                          size={24}
                          fontSize="12px"
                          whiteText
                        />
                        <span className="font-['Outfit'] font-light text-[14px] leading-[20px] text-[#F9FBFE] whitespace-nowrap">
                          {c.firstname} {c.lastname}
                        </span>
                        <button onClick={() => toggleContact(c.userId)}>
                          <CrossIconSecondary className="text-[#F9FBFE]" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="wa-group-scroll max-h-[290px] overflow-y-auto -mx-2 px-2">
                  {contacts.length === 0 ? (
                    <div className="py-8 text-center out-300-14 text-gray-500">
                      No contacts yet. Start a direct message with someone
                      first, then you can add them to a group.
                    </div>
                  ) : (
                    // Same letter-header UI whether browsing or searching.
                    // The only difference is section ORDER: letterSections
                    // is alphabetical when browsing, and relevance-ranked
                    // (following filteredContacts) when searching — see the
                    // letterSections useMemo above.
                    letterSections.map((section, idx) => (
                      <div key={`${section.letter}-${idx}`}>
                        <div className="text-gray-500 out-500-12 text-[14px] px-2 ">
                          {section.letter}
                        </div>
                        {section.items.map((c: any) => (
                          <ContactRow
                            key={c.userId}
                            contact={c}
                            checked={selectedIds.includes(c.userId)}
                            onCheck={toggleContact}
                          />
                        ))}
                      </div>
                    ))
                  )}
                </div>

                <div className="flex justify-end gap-x-3 mt-auto pt-4">
                  <CustomButton
                    text="Cancel"
                    className="bg-transparent text-gray-500"
                    height="30px"
                    width="64px"
                    onClick={onClose}
                  />
                  <CustomButton
                    text="Next"
                    height="30px"
                    width="50px"
                    disabled={selectedIds.length === 0}
                    onClick={() => setStep(2)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col h-full" style={{ height: 481 }}>
                  <div className="flex flex-col gap-y-4">
                    <div className="flex ml-20 w-full h-[165px] items-start">
                      <ImageCropModal cropShape={"round"}>
                        <Upload
                          action={process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej"}
                          customRequest={uploadImage}
                          onChange={handleImageChange}
                          multiple={false}
                          showUploadList={false}
                        >
                          <div
                            className={`wa-group-upload rounded-full flex items-center justify-center cursor-pointer overflow-hidden ${image ? "" : "border border-dashed border-gray-300"
                              }`}
                            style={{ width: 165, height: 165 }}
                          >
                            {image ? (
                              <img
                                src={image}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-['Outfit'] font-medium text-[14px] leading-[20px] text-[#667085] text-center px-4">
                                Upload Group Icon
                              </span>
                            )}
                          </div>
                        </Upload>
                      </ImageCropModal>
                    </div>

                    <div>
                      <p
                        className="out-300-14 pb-[6px]"
                        style={{ color: "#292927" }}
                      >
                        Name of the group
                      </p>
                      <CustomInputBox
                        placeholder="e.g. Design team"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        wrapperClassName="wa-group-name-input"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-x-3 mt-auto">
                    <CustomButton
                      text="Cancel"
                      className="bg-transparent text-gray-500"
                      height="30px"
                      width="64px"
                      onClick={onClose}
                    />
                    <CustomButton
                      text="Create Group"
                      height="30px"
                      width="120px"
                      disabled={!groupName.trim() || creating}
                      onClick={handleCreateGroup}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />
    </>
  );
};

export default CreateGroupModal;